/**
 * AI Skills Manager Component
 *
 * Purpose: List + toolbar + MDTable for AI Skills with create / edit / delete.
 *          Mirrors AIAgentManager structure, minus tenant/scope/override (skills
 *          are GLOBAL-only).
 * Author: Development Team
 * Created: 2026-06-10
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import { RowActions } from '../common';

import MDTable from '../md-table';
import { TableColumn } from '../../types/table';
import aiSkillService, { AiSkill, AiSkillBlockingAgent } from '../../services/aiSkillService';
import { formatAuditTimestamp, formatAuditUser } from '../../utils/formatting';
import AISkillModal from './modals/AISkillModal';
import { SecondaryButton } from '../common/ActionButtons';

interface AISkillsManagerProps {
  canEdit: boolean;
}

const MD_PREVIEW_MAX = 100;

function mdPreview(value: string | undefined): string {
  if (!value) return '—';
  const collapsed = value.replace(/\s+/g, ' ').trim();
  if (!collapsed) return '—';
  return collapsed.length > MD_PREVIEW_MAX ? `${collapsed.slice(0, MD_PREVIEW_MAX)}…` : collapsed;
}

const AISkillsManager: React.FC<AISkillsManagerProps> = ({ canEdit }) => {
  const [data, setData] = useState<AiSkill[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [selectedSkill, setSelectedSkill] = useState<AiSkill | undefined>();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingSkill, setDeletingSkill] = useState<AiSkill | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [blockingAgents, setBlockingAgents] = useState<AiSkillBlockingAgent[]>([]);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await aiSkillService.listAISkills();
      setData(response || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load AI Skills');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreate = () => {
    setSelectedSkill(undefined);
    setModalMode('create');
    setModalOpen(true);
  };

  const handleEdit = (skill: AiSkill) => {
    setSelectedSkill(skill);
    setModalMode('edit');
    setModalOpen(true);
  };

  const handleDeleteRequest = (skill: AiSkill) => {
    setDeletingSkill(skill);
    setDeleteError(null);
    setBlockingAgents([]);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingSkill) return;
    setDeleteLoading(true);
    setDeleteError(null);
    try {
      await aiSkillService.deleteAISkill(deletingSkill.id);
      setDeleteDialogOpen(false);
      setDeletingSkill(null);
      await loadData();
    } catch (err) {
      const conflict = aiSkillService.extractConflict(err);
      if (conflict && conflict.error === 'skill_in_use') {
        setDeleteError(conflict.message);
        setBlockingAgents(conflict.blocking_agents || []);
      } else {
        setDeleteError(
          err instanceof Error ? err.message : 'Failed to delete skill. Please try again.'
        );
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  const columns: TableColumn[] = [
    {
      field: 'name',
      headerName: 'Skill Name',
      width: 240,
      sortable: true,
      renderCell: (value: string) => <Typography variant="body2">{value}</Typography>,
    },
    {
      field: 'md_content',
      headerName: 'MD Content',
      width: 360,
      sortable: false,
      renderCell: (value: string) => (
        <Typography
          variant="body2"
          color="textSecondary"
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {mdPreview(value)}
        </Typography>
      ),
    },
    {
      field: 'id',
      headerName: 'ID',
      width: 300,
      sortable: true,
      renderCell: (value: string) => (
        <Box sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }} data-testid="ai-skill-id-cell">
          {value}
        </Box>
      ),
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 140,
      sortable: true,
      renderCell: (value: string) => formatAuditTimestamp(value),
    },
    {
      field: 'created_by',
      headerName: 'Created By',
      width: 140,
      sortable: true,
      renderCell: (value: string, row: AiSkill) => formatAuditUser(value, row.created_by_user),
    },
    {
      field: 'updated_at',
      headerName: 'Modified Date',
      width: 140,
      sortable: true,
      renderCell: (value: string) => formatAuditTimestamp(value),
    },
    {
      field: 'updated_by',
      headerName: 'Modified By',
      width: 140,
      sortable: true,
      renderCell: (value: string, row: AiSkill) => formatAuditUser(value, row.updated_by_user),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      renderCell: (_v: any, row: AiSkill) => (
        <RowActions
          entityLabel="AI Skill"
          rowLabel={row.name}
          testIdPrefix="ai-skill"
          rowTestId={`ai-skill-row-${row.id}`}
          onEdit={() => handleEdit(row)}
          onDelete={() => handleDeleteRequest(row)}
          deleteDisabled={!canEdit}
        />
      ),
    },
  ];

  return (
    <Box>
      <MDTable
        mode="manual"
        manual={{
          columns,
          title: 'AI Skills',
          description: `${data.length} skill(s) found`,
        }}
        data={data.map((item) => ({ ...item }))}
        loading={loading}
        error={error}
        onRowClick={handleEdit}
        onRefresh={loadData}
        onAddNew={canEdit ? handleCreate : undefined}
        showRefreshButton={true}
        showAddButton={canEdit}
        selectable={false}
        defaultSortModel={[{ field: 'name', sort: 'asc' }]}
        sortable={true}
        filterable={true}
        defaultColumnVisibility={{
          id: false,
          created_at: false,
          created_by: false,
        }}
      />

      <AISkillModal
        open={modalOpen}
        mode={modalMode}
        skill={selectedSkill}
        canEdit={canEdit}
        onClose={() => setModalOpen(false)}
        onSaved={loadData}
        onDelete={(skill) => {
          setModalOpen(false);
          handleDeleteRequest(skill);
        }}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Skill</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the skill &quot;{deletingSkill?.name}&quot;? This cannot
            be undone.
          </DialogContentText>
          {deleteError && (
            <Box sx={{ mt: 2 }} data-testid="ai-skill-delete-error">
              <Typography variant="body2" color="error">
                {deleteError}
              </Typography>
              {blockingAgents.length > 0 && (
                <List dense>
                  {blockingAgents.map((a) => (
                    <ListItem key={`${a.ai_agent_id}-${a.tenant_id}`} sx={{ py: 0 }}>
                      <ListItemText
                        primary={a.label}
                        secondary={`${a.ai_agent_id} · ${a.tenant_id}`}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <SecondaryButton onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>
            Cancel
          </SecondaryButton>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteLoading}
            data-testid="confirm-delete-ai-skill"
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AISkillsManager;
